import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { CognitoUser } from '@/hooks/useAuth';
import { CreateAccount } from '@/hooks/useCreateAccount';

/**
 * The authenticated user's ID. Set during login, cleared on logout.
 *
 * Can contain numeric DB ID e.g. "123" or Cognito UUID for pending OAuth.
 * See util/authTokens.ts for details and helper functions.
 *
 * **When to use this atom:**
 * - Bearer token authentication: middleware, API client setup
 * - Session management: login and logout flows
 * - Writing to auth-scoped storage
 *
 * **When NOT to use this atom:**
 * - Displaying user data in UI: use `userIdAtom` instead
 * - Fetching user-scoped resources: use `userIdAtom` instead
 * - Any feature that should respect account impersonation: use `userIdAtom`
 *
 * @see {@link userIdAtom} for the identity atom that respects impersonation
 * @see {@link actAsUserIdAtom} for the impersonation state atom
 */
export const authenticatedUserIdAtom = atom<CognitoUser['userId'] | undefined>(undefined);

export const authStateResolvedAtom = atom<boolean>(false);

export const createAccountAtom = atomWithReset<CreateAccount>({
  email: '',
});

/**
 * When set, API requests will include an X-Act-As header
 * allowing support team members to act on behalf of support accounts.
 * Value is the user ID of the account being impersonated.
 */
export const actAsUserIdAtom = atom<string | undefined>(undefined);

/**
 * The active user ID — impersonated account if set, otherwise the authenticated user.
 *
 * This is the default identity atom. Most code should read this atom to get
 * the "current" user, which respects account impersonation automatically.
 *
 * @see {@link actAsUserIdAtom} for the impersonation state; takes precedence when set
 * @see {@link authenticatedUserIdAtom} for the real authenticated user as fallback value
 */
export const userIdAtom = atom<string | undefined>(
  (get) => get(actAsUserIdAtom) || get(authenticatedUserIdAtom)
);

/**
 * Stores a redirect path from `redirectAfterConfirmation` URL param (e.g. from magic links).
 * When set, the update-password page will redirect here instead of directory after completion.
 * Cleared after use.
 */
export const redirectAfterConfirmationAtom = atom<string | null>(null);
